/** The Days card's structural edits (plan §5 "Edit", O7), each ONE draft `SET` — one undo
 *  step — on the records outline's operations (`addRow` / `removeRow` / `restoreRow` /
 *  `moveRow`), with the itinerary's words: the undo button's label and the sentence the card's
 *  live region says ("Removed Day 2 · Ayutthaya", "Moved “Bangkok” to Day 2").
 *
 *  - **Add** appends a day (or inserts it after one): a negative id from `nextTempId` over the
 *    itinerary's ONE membership set; it reads `NEW_DAY` until edited. Never past the 365-day
 *    cap — `addDayRefusal` says why.
 *  - **Remove** takes a day off the list and reverts its own edits: a saved day is deleted on
 *    save (shown struck until then; on the wire, its six fields set to null, each
 *    compare-and-set — `toItineraryChangeset`), a new day simply goes.
 *  - **Restore** puts a saved day back where it was stored.
 *  - **Move** changes its place (Alt+↑/↓, the row menu, a drag) — a membership change only.
 *
 *  Pure over the draft state and the baselines. */
import { ITINERARY_DAYS_CAP } from '@/api/itineraryRecord';
import type { OutlineOpsContext, OutlineSetAction } from '@/features/records/outline/ops';
import { addRow, moveRow, removeRow, restoreRow } from '@/features/records/outline/ops';
import { listIn, valueIn } from '@/features/records/outline/tree';

import { DAYS_PATH, dayRec, dayRows, dayRowName, DAYS_OUTLINE, type DayRowModel } from './daysModel';

/** A structural edit as the card runs it: the action, what the live region says, and the day
 *  it concerns (a new day's temporary id). */
export interface DayEdit {
  action: OutlineSetAction;
  said: string;
  dayId: number;
}

const DAY_LEVEL = 0;

const rowsOf = (ctx: OutlineOpsContext): DayRowModel[] => dayRows(ctx.state.entries, ctx.baselines, ctx.ownerRec);

/** A day's name as the draft has it ("Day 3–4 · Ayutthaya"; a removed day by its title). */
export function dayNameIn(ctx: OutlineOpsContext, dayId: number): string {
  const row = rowsOf(ctx).find((r) => r.id === dayId);
  if (row) return dayRowName(row);
  const title = valueIn(DAYS_OUTLINE, ctx.state.entries, ctx.baselines, dayRec(dayId), 'title');
  return (typeof title === 'string' && title.trim()) || 'the day';
}

/** A day's title for a sentence: “Bangkok”, or its span label when untitled. */
function quoted(ctx: OutlineOpsContext, dayId: number): string {
  const row = rowsOf(ctx).find((r) => r.id === dayId);
  return row?.title ? `“${row.title}”` : (row ? dayRowName(row) : 'the day');
}

/** The days the itinerary keeps (not counting the removed ones). */
export function keptDays(ctx: Pick<OutlineOpsContext, 'state' | 'baselines' | 'ownerRec'>): number {
  return listIn(DAYS_OUTLINE, ctx.state.entries, ctx.baselines, ctx.ownerRec, DAYS_PATH).length;
}

/** Why no day can be added, or null: the 365-day cap the changeset enforces. */
export function addDayRefusal(kept: number): string | null {
  return kept >= ITINERARY_DAYS_CAP ? `An itinerary holds at most ${ITINERARY_DAYS_CAP} days` : null;
}

/** A new day at the end — or right after `afterId`. Null at the cap. */
export function addDay(ctx: OutlineOpsContext, { afterId }: { afterId?: number } = {}): DayEdit | null {
  if (addDayRefusal(keptDays(ctx))) return null;
  const list = listIn(DAYS_OUTLINE, ctx.state.entries, ctx.baselines, ctx.ownerRec, DAYS_PATH);
  const after = afterId === undefined ? -1 : list.indexOf(afterId);
  const at = after >= 0 ? after + 1 : undefined;
  const label = at === undefined ? `Add day ${list.length + 1}` : `Insert a day after ${dayNameIn(ctx, afterId!)}`;
  const { id, action } = addRow(ctx, DAY_LEVEL, ctx.ownerRec, { label, ...(at !== undefined ? { at } : {}) });
  const next = { ...ctx, state: { ...ctx.state, entries: applyEntries(ctx, action) } };
  return { action, said: `Added ${dayNameIn(next, id)}`, dayId: id };
}

/** Take a day off the itinerary (a saved one is deleted on save). Null when it is not there. */
export function removeDay(ctx: OutlineOpsContext, dayId: number): DayEdit | null {
  const name = dayNameIn(ctx, dayId);
  const r = removeRow(ctx, dayRec(dayId), `Remove ${name}`);
  if (!r || 'refused' in r) return null;
  return { action: r.action, said: `Removed ${name}`, dayId };
}

/** Put a removed saved day back where it was stored. Null when it is not removed. */
export function restoreDay(ctx: OutlineOpsContext, dayId: number): DayEdit | null {
  if (listIn(DAYS_OUTLINE, ctx.state.entries, ctx.baselines, ctx.ownerRec, DAYS_PATH).includes(dayId)) return null;
  const r = restoreRow(ctx, DAY_LEVEL, ctx.ownerRec, dayId, { label: `Restore ${dayNameIn(ctx, dayId)}` });
  if ('refused' in r) return null;
  const next = { ...ctx, state: { ...ctx.state, entries: applyEntries(ctx, r.action) } };
  return { action: r.action, said: `Restored ${dayNameIn(next, dayId)}`, dayId };
}

/** Move a day to `toIndex` among the kept days. Null when it cannot move there. */
export function moveDay(ctx: OutlineOpsContext, dayId: number, toIndex: number): DayEdit | null {
  const what = quoted(ctx, dayId);
  const list = listIn(DAYS_OUTLINE, ctx.state.entries, ctx.baselines, ctx.ownerRec, DAYS_PATH);
  const from = list.indexOf(dayId);
  if (from < 0) return null;
  const r = moveRow(ctx, dayRec(dayId), toIndex, `Move ${what} ${toIndex < from ? 'up' : 'down'}`);
  if (!r || 'refused' in r) return null;
  const next = { ...ctx, state: { ...ctx.state, entries: applyEntries(ctx, r.action) } };
  const row = rowsOf(next).find((x) => x.id === dayId);
  return { action: r.action, said: `Moved ${what} to ${row?.range ? dayRowName({ range: row.range, title: '' }) : `day ${toIndex + 1}`}`, dayId };
}

/** The entries a SET leaves (a preview of the action, for the sentence it says): each change's
 *  `to` over the draft's. Only what the sentence reads — the day list — matters. */
function applyEntries(ctx: OutlineOpsContext, action: OutlineSetAction): OutlineOpsContext['state']['entries'] {
  const entries = { ...ctx.state.entries };
  for (const c of action.changes) {
    const rec = { ...(entries[c.rec] ?? {}) };
    rec[c.path] = { from: c.from, to: c.to };
    entries[c.rec] = rec;
  }
  return entries;
}
